// Pont natif. Seul ce fichier importe des paquets npm ; il est bundlé par
// esbuild vers www/vendor/native.js et expose window.GXQ_NATIVE.
//
// Le reste de l'app reste en modules ES bruts, sans build : ainsi le PWA
// continue de se modifier et de se déployer par simple copie de fichiers.

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { BackgroundGeolocation } from '@capgo/background-geolocation';
import { AppLauncher } from '@capacitor/app-launcher';
import { LocalNotifications } from '@capacitor/local-notifications';

const isNative = Capacitor.isNativePlatform();

/* ---------------- localisation ---------------- */

/**
 * Démarre le mode balade.
 *
 * On ne demande QUE la localisation de premier plan et les notifications.
 * ACCESS_BACKGROUND_LOCATION n'est volontairement pas déclaré : le service de
 * premier plan lancé pendant que l'app est ouverte garde le GPS actif écran
 * éteint, sans le dialogue « Toujours autoriser », sans la justification écrite
 * à Google, et sans la revue manuelle de trois à cinq jours qu'elle impose à
 * chaque publication.
 *
 * La contrepartie est assumée : l'usager doit démarrer sa balade lui-même.
 */
async function startWatch(onFix, onError) {
  const status = await BackgroundGeolocation.requestPermissions({
    permissions: ['location', 'notification']
  });
  if (status.location === 'denied') {
    onError({ message: 'Localisation refusée', denied: true });
    return false;
  }

  await BackgroundGeolocation.start(
    {
      // La présence de backgroundMessage est ce qui déclenche le service de
      // premier plan Android : sans elle, le suivi s'arrête en arrière-plan.
      backgroundTitle: 'Balade en cours',
      backgroundMessage: 'GExplore veille sur les lieux à proximité.',
      requestPermissions: false,
      stale: false,
      // Le seuil utile est celui du réarmement des zones, pas celui du tracé :
      // on n'enregistre aucun trajet. 100 m suffit à suivre le déplacement sans
      // réveiller le WebView à chaque virage.
      distanceFilter: 100
    },
    (location, error) => {
      if (error) {
        onError({
          code: error.code,
          message: error.message,
          denied: error.code === 'NOT_AUTHORIZED'
        });
        return;
      }
      if (!location) return;
      onFix({
        lat: location.latitude,
        lng: location.longitude,
        accuracy: location.accuracy,
        speed: location.speed,
        timestamp: location.time || Date.now()
      });
    }
  );
  return true;
}

const stopWatch = () => BackgroundGeolocation.stop();
const openSettings = () => BackgroundGeolocation.openSettings();

/* ---------------- zones de géorepérage ---------------- */

let geofencingReady = false;

async function setupGeofencing() {
  if (geofencingReady) return;
  await BackgroundGeolocation.setupGeofencing({
    notifyOnEntry: true,
    // Les sorties ne servent qu'à la zone-sentinelle, qui déclenche le
    // réarmement. Les lieux eux-mêmes n'ont rien à signaler quand on les quitte.
    notifyOnExit: true,
    requestPermissions: false,
    // Sans ACCESS_BACKGROUND_LOCATION, activer ceci ne ferait qu'échouer en
    // silence. Les zones restent le filet de sécurité du mode balade, pas un
    // mécanisme de veille permanente.
    backgroundLocation: false
  });
  geofencingReady = true;
}

async function addGeofence({ identifier, lat, lng, radius, payload }) {
  await BackgroundGeolocation.addGeofence({
    identifier,
    latitude: lat,
    longitude: lng,
    radius,
    payload: payload || {}
  });
}

const removeGeofence = (identifier) =>
  BackgroundGeolocation.removeGeofence({ identifier }).catch(() => {});

const removeAllGeofences = () => BackgroundGeolocation.removeAllGeofences().catch(() => {});

async function monitoredGeofences() {
  const { identifiers } = await BackgroundGeolocation.getMonitoredGeofences().catch(() => ({
    identifiers: []
  }));
  return identifiers || [];
}

function onGeofenceTransition(callback) {
  BackgroundGeolocation.addListener('geofenceTransition', (event) => {
    callback({
      identifier: event.identifier,
      entering: event.transition === 'enter',
      payload: event.payload || {}
    });
  });
  BackgroundGeolocation.addListener('geofenceError', (event) => {
    console.warn('zone en erreur', event.identifier, event.message);
  });
}

/* ---------------- notifications ---------------- */

let notificationsReady = false;

async function ensureNotifications() {
  if (notificationsReady) return true;
  const granted = await LocalNotifications.requestPermissions()
    .then((r) => r.display === 'granted')
    .catch(() => false);
  if (!granted) return false;

  await LocalNotifications.registerActionTypes({
    types: [{ id: 'POI', actions: [{ id: 'ignore', title: 'Pas cette fois' }] }]
  }).catch(() => {});
  notificationsReady = true;
  return true;
}

/**
 * Notifie un lieu à proximité.
 *
 * `extra.poiId` est ce que l'app relit à l'ouverture pour router vers la bonne
 * fiche. L'identifiant de notification est celui du lieu : deux notifications
 * pour le même lieu se remplacent au lieu de s'empiler.
 */
async function notifyPoi({ id, title, body }) {
  if (!(await ensureNotifications())) return false;
  await LocalNotifications.schedule({
    notifications: [
      {
        id,
        title,
        body,
        actionTypeId: 'POI',
        autoCancel: true,
        extra: { poiId: id }
      }
    ]
  });
  return true;
}

function onNotificationTap(callback) {
  LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
    if (event.actionId === 'ignore') return;
    const poiId = event.notification?.extra?.poiId ?? event.notification?.id;
    if (poiId) callback(poiId);
  });
}

/* ---------------- système ---------------- */

// Confie l'URL au système plutôt qu'au WebView : c'est ce qui fait passer la
// main à Waze ou à Google Maps.
async function openExternal(url) {
  const { completed } = await AppLauncher.openUrl({ url });
  return completed;
}

// Android 11+ filtre la visibilité des paquets : sans les <queries> du
// manifeste, ceci répondrait faux même avec l'application installée.
async function isAppInstalled(packageName) {
  const { value } = await AppLauncher.canOpenUrl({ url: packageName }).catch(() => ({
    value: false
  }));
  return value;
}

async function getLaunchUrl() {
  const result = await App.getLaunchUrl().catch(() => null);
  return result?.url || null;
}

function onLaunchIntent(callback) {
  App.addListener('appUrlOpen', (event) => {
    if (event?.url) callback(event.url);
  });
}

function onBackButton(callback) {
  App.addListener('backButton', ({ canGoBack }) => callback(canGoBack));
}

// L'app revient-elle au premier plan ? Le retour est le moment de rafraîchir
// l'affichage des distances, que le service a fait vieillir en arrière-plan.
function onResume(callback) {
  App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) callback();
  });
}

const exitApp = () => App.exitApp();

window.GXQ_NATIVE = {
  isNative,
  platform: Capacitor.getPlatform(),
  startWatch,
  stopWatch,
  openSettings,
  setupGeofencing,
  addGeofence,
  removeGeofence,
  removeAllGeofences,
  monitoredGeofences,
  onGeofenceTransition,
  notifyPoi,
  onNotificationTap,
  openExternal,
  isAppInstalled,
  getLaunchUrl,
  onLaunchIntent,
  onBackButton,
  onResume,
  exitApp
};
